/**
 * PromCollectionSubtab - PathwayView nested PROM admin schedule (Sprint
 * 4.1 REDO).
 *
 * Renders the four-step TLP1 PROM admin schedule from the prototype:
 *   - Step 1 Complete (MSQ / PHQ-9 / SF-12)
 *   - Step 2 Scheduled Apr 19 (PHQ-9 / SF-12 / DQOLY)
 *   - Step 3 Scheduled May 3 (GSRS / IWQOL / PHQ-9)
 *   - Step 4 Scheduled Jul 19 (full instrument bundle)
 *
 * Instrument chips share the visual language of NbaCard / Sara R PROM
 * surfaces: `rounded-signal-pill`, `bg-surface-muted`, `text-text-secondary`.
 * The complete-step badge maps to the Signal DS `status-active` token.
 *
 * HC5: zero em / en dashes.
 * HC6 a11y: each row is `role="listitem"` inside a `role="list"`; the
 * step ordinal + status label is read aloud first, the instrument chip
 * group has its own aria-label so the screen reader says
 * "Step 1, Complete, instruments MSQ PHQ-9 SF-12".
 */
import {
  PROM_COLLECTION_STEPS,
  type PromCollectionStep,
  type PromStepStatus,
} from './fixtures';

const STATUS_BADGE_TONE: Record<
  PromStepStatus,
  { bgClass: string; fgClass: string; symbol: string }
> = {
  complete: {
    bgClass: 'bg-status-active-bg',
    fgClass: 'text-status-active-fg',
    // Hyphen-arrow check mark via aria-hidden Unicode glyph; no em dash.
    symbol: '✓', // U+2713 CHECK MARK
  },
  scheduled: {
    bgClass: 'bg-surface-muted',
    fgClass: 'text-text-secondary',
    symbol: '',
  },
};

function StepRow({ step }: { step: PromCollectionStep }) {
  const tone = STATUS_BADGE_TONE[step.status];
  return (
    <li
      role="listitem"
      data-prom-step-status={step.status}
      data-prom-step-ordinal={step.ordinal}
      className="flex flex-col gap-gutter-sm rounded-signal-md bg-surface-base p-gutter-md shadow-signal-card ring-1 ring-inset ring-surface-border md:flex-row md:items-center md:justify-between"
    >
      <div className="flex items-center gap-gutter-md">
        <span
          aria-hidden="true"
          className="flex h-8 w-8 items-center justify-center rounded-signal-pill bg-surface-muted text-signal-sm font-semibold text-text-primary"
        >
          {step.ordinal}
        </span>
        <div className="flex flex-col">
          <span className="text-signal-base font-semibold text-text-primary">
            Step {step.ordinal}
          </span>
          <span className="text-signal-xs text-text-tertiary">
            {step.dateLabel}
          </span>
        </div>
        <span
          data-prom-step-badge={step.status}
          className={[
            'rounded-signal-pill px-gutter-sm py-1 text-signal-xs font-semibold uppercase tracking-wide',
            tone.bgClass,
            tone.fgClass,
          ].join(' ')}
        >
          {tone.symbol ? (
            <span aria-hidden="true" className="mr-1">
              {tone.symbol}
            </span>
          ) : null}
          {step.statusLabel}
        </span>
      </div>
      <ul
        role="list"
        aria-label={`Step ${step.ordinal} instruments`}
        className="flex flex-wrap items-center gap-gutter-sm"
      >
        {step.instruments.map((instrument) => (
          <li
            key={instrument}
            role="listitem"
            data-prom-instrument-chip={instrument}
            className="rounded-signal-pill bg-surface-muted px-gutter-sm py-1 text-signal-xs font-medium text-text-secondary ring-1 ring-inset ring-surface-border"
          >
            {instrument}
          </li>
        ))}
      </ul>
    </li>
  );
}

export interface PromCollectionSubtabProps {
  className?: string;
}

export function PromCollectionSubtab({ className }: PromCollectionSubtabProps) {
  const composed = [
    'flex flex-col gap-gutter-md',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <section
      aria-labelledby="prom-collection-heading"
      data-prom-collection-subtab="root"
      className={composed}
    >
      <header className="flex flex-col gap-gutter-sm">
        <h3
          id="prom-collection-heading"
          className="text-signal-xl font-semibold text-text-primary"
        >
          PROM Collection - Cardiometabolic Pathway
        </h3>
        <p className="text-signal-sm text-text-secondary">
          Four-step admin schedule. Instruments dispatched via the Scrips
          PROM Engine; FHIR Observation output once results return.
        </p>
      </header>
      <ul
        role="list"
        aria-label="PROM Collection schedule steps"
        className="flex flex-col gap-gutter-sm"
      >
        {PROM_COLLECTION_STEPS.map((step) => (
          <StepRow key={step.id} step={step} />
        ))}
      </ul>
    </section>
  );
}
